import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { Tenant, ToastMessage } from '../../types.js';
import { TenantTargetSelect } from './PartnerTenantCreditsTab.js';

export function PartnerSignupTab({
  http,
  tenants,
  tenantId,
  onTenantChange,
  onToast,
}: {
  http: AdminHttpClient;
  tenants: Tenant[];
  tenantId: string;
  onTenantChange: (id: string) => void;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const tenant = tenants.find((item) => item.id === tenantId);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setEnabled(tenant?.selfSignupEnabled ?? false), [tenant]);

  async function update(next: boolean) {
    if (!tenantId) return;
    setBusy(true);
    try {
      const updated = await http.partnerSetTenantSelfSignup(tenantId, next);
      setEnabled(updated.selfSignupEnabled ?? false);
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({
        tone: 'error',
        message: describeError(error, (key, options) => (options ? t(key, options) : t(key))),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="content-stack">
      <TenantTargetSelect
        label={t('tenantScope.signupPolicyTarget')}
        emptyLabel={t('tenantScope.selectTarget')}
        tenants={tenants}
        tenantId={tenantId}
        onChange={onTenantChange}
      />
      {tenant && (
        <section className="panel form-grid">
          <h2>{t('selfSignup.title')}</h2>
          <p className="screen-help">
            {tenant.selfSignupAllowed ? t('selfSignup.masterOn') : t('selfSignup.masterOff')}
          </p>
          <label className="check-row">
            <input
              type="checkbox"
              checked={enabled}
              disabled={busy}
              onChange={(event) => void update(event.target.checked)}
            />
            <span>
              <strong>{t('selfSignup.enabled')}</strong>
              <small>{t('selfSignup.enabledHelp')}</small>
            </span>
          </label>
        </section>
      )}
    </section>
  );
}
